import { useEffect, useState } from "react";
import { intl } from "LocaleWrapper";
import { CustomDialog, FormDialog } from "components/dialogs/Dialogs";
import CargoCreateEdit from "view/cargos/CargoCreateEdit";
import { CargoActionForm, CargoForm, CargoLinkedCharter } from "types/index";
import CargoCreateEditCustomContent from "view/cargos/components/dialogs/CargoCreateEditCustomContent";
import { Fields, hasOptionalFieldPermission, hasPluginPermission, Language } from "utils";
import {
	getCargoFormDefaultValues,
	getCargoFormMutationInput,
} from "view/cargos/utils/CargoUtils";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { cargoForm_query } from "view/cargos/utils/CargoQueries";
import { useForm, useWatch } from "react-hook-form";
import { useStoreState } from "ezStore";
import { Button } from "@cofano/cck";
import { SalesCharterStatus, AgreementTypeEnum } from "__generated__/enums";
import {
	getDefaultCargoActionTypes_query,
	updateDefaultCargoActionTypes_mutation,
} from "view/agreements/utils/AgreementQueries";
import { validateCargoActions } from "view/cargos/utils/CargoActionUtils";
import moment from "moment";
import { DateUtils } from "utils/DateUtils.tsx";
import { SaveCargoFormDocument, SaveCargoFormMutation } from "__generated__/view/cargos/graphql/cargo.mutations.generated";
import { ChildProps } from "types/commons";
import { useTheme } from "@mui/material";

// eslint-disable-next-line react-refresh/only-export-components
export enum CargoDialogType {
	CUSTOM = "CUSTOM",
	PAGE = "PAGE"
}

type FormCargo = ChildProps<SaveCargoFormMutation, "saveCargo">;

export type CargoCreateEditDialogProps = {
	open: boolean;
	onClose: (openEmailDialog: boolean) => void;
	cargoId?: number;
	charter?: CargoLinkedCharter;
	refetch: (response: FormCargo) => void;
	type: CargoDialogType;
	validFrom?: number | null;
	validUntil?: number | null;
};

const CargoCreateEditDialog = ({ open, onClose, cargoId, charter, refetch, type, validFrom, validUntil }: CargoCreateEditDialogProps) => {
	const Component = (type === CargoDialogType.CUSTOM) ? CustomEditableDialog : PageDialog;

	return (
		<Component
			open={open}
			onClose={onClose}
			cargoId={cargoId}
			charter={charter}
			refetch={refetch}
			validFrom={validFrom}
			validUntil={validUntil}
		/>
	);
};

const CustomEditableDialog = ({ open, onClose, cargoId, charter, refetch, validFrom, validUntil }: Omit<CargoCreateEditDialogProps, "type">) => {
	const session = useStoreState(state => state.session);
	const demurrageAgreement = charter?.contract?.agreements?.find(x => x.type === AgreementTypeEnum.DemurrageCalculation);

	const theme = useTheme();

	const useSimulatedTimestamps = hasPluginPermission(session, "cargoActionUseSimulatedTimestamps");

	const [ getCargo, { loading: getCargoLoading } ] = useLazyQuery(cargoForm_query);
	const [ saveCargo, { loading: saveCargoLoading } ] = useMutation(SaveCargoFormDocument);

	const { data: getDefaultActionTypes } = useQuery(getDefaultCargoActionTypes_query, {
		variables: {
			agreementId: Number(demurrageAgreement?.id),
		},
		skip: demurrageAgreement == null || cargoId != null,
	});
	const [ updateDefaultCargoActionTypes ] = useMutation(updateDefaultCargoActionTypes_mutation);

	const loading = getCargoLoading || saveCargoLoading;
	const defaultIncludedActionTypes = getDefaultActionTypes?.defaultCargoActionTypesForDemurrage ?? undefined;

	const { control, handleSubmit, formState, reset, setValue, watch, trigger, getValues, setError, clearErrors } = useForm<CargoForm>({
		defaultValues: getCargoFormDefaultValues(session, charter),
		mode: "onChange",
	});

	const actions = useWatch({
		control: control,
		name: "actions",
	}) ?? [];

	const beforeSave = async (data: CargoForm, openEmailDialog?: boolean) => {
		const isFormValid = await trigger();

		if (!isFormValid) {
			return;
		}

		const actionsValid = onValidateCargoActions();
		if (!actionsValid) {
			return;
		}

		onSave(data, openEmailDialog);
	};

	const onValidateCargoActions = () => {
		return validateCargoActions(actions, setError, clearErrors, useSimulatedTimestamps, getValues);
	};

	const onSave = (data: CargoForm, openEmailDialog?: boolean) => {
		saveCargo({
			variables: {
				input: getCargoFormMutationInput(data, useSimulatedTimestamps),
			},
		}).then((response) => {
			// update laytime default cargo action types
			if (charter != null && demurrageAgreement != null) {
				updateDefaultCargoActionTypes({
					variables: {
						charterId: charter.id,
						agreementId: demurrageAgreement.id,
					},
				});
			}

			onClose(openEmailDialog ?? false);
			if (response.data) {
				refetch(response.data.saveCargo);
			}
		});
	};

	const handleOnClose = () => {
		onClose(false);
		reset(getCargoFormDefaultValues(session, charter));
	};

	useEffect(() => {
		if (open && cargoId) {
			getCargo({
				variables: {
					id: Number(cargoId),
				},
			}).then(response => {
				reset({
					...response.data?.cargo,
					isWeightPrimary: response.data?.cargo?.isWeightPrimary != null ? response.data.cargo.isWeightPrimary : hasPluginPermission(session, "cargoUsePrimaryWeight"),
					isVolumePrimary: response.data?.cargo?.isVolumePrimary != null ? response.data.cargo.isVolumePrimary : hasPluginPermission(session, "cargoUsePrimaryVolume"),
					actions: response.data?.cargo?.actions
						.sort((a, b) =>  (a.start ?? Infinity) - (b.start ?? Infinity))
						.map((action): CargoActionForm => ({
							...action,
							date: !action.displayAsWindow && !!action.start ? moment(action.start).toDate().toDateString() : undefined,
							windowFrom: action.displayAsWindow ? DateUtils.getTimestamp(action.start) : undefined,
							windowTo: action.displayAsWindow ? DateUtils.getTimestamp(action.end) : undefined,
						}) as CargoActionForm)
					,
				} as CargoForm);
			});
		}
	}, [ open ]);

	return <CustomDialog
		maxWidth={hasOptionalFieldPermission(session, Fields.ORDER, "services") ? "xl" : "lg"}
		open={open}
		onClose={handleOnClose}
		title={Language("CARGO")}
		dialogStyling={{ content: { backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#EEE" } }}
		actions={<>
			<Button
				onClick={handleSubmit(cargo => beforeSave(cargo, false))}
				loading={loading}
				size="small"
			>
				{intl.formatMessage({ id: "SAVE" })}
			</Button>

			{charter?.status && [ SalesCharterStatus.Planned, SalesCharterStatus.Confirmed, SalesCharterStatus.InProgress, SalesCharterStatus.ExecutionCompleted ].includes(charter.status.name) &&
				<Button
					onClick={handleSubmit(cargo => beforeSave(cargo, true))}
					loading={loading}
					size="small"
				>
					{intl.formatMessage({ id: "SAVE_AND_SEND_UPDATE" })}
				</Button>
			}
		</>}
	>
		<form id="cargoForm">
			<CargoCreateEditCustomContent
				defaultIncludedActionTypes={defaultIncludedActionTypes}
				open={open}
				onClose={onClose}
				refetch={refetch}
				watch={watch}
				control={control}
				formState={formState}
				setValue={setValue}
				validFrom={validFrom}
				validUntil={validUntil}
				trigger={trigger}
				getValues={getValues}
				validateCargoActions={onValidateCargoActions}
			/>
		</form>
	</CustomDialog>
	;
};

const PageDialog = ({ open, onClose, cargoId, charter, refetch, validFrom, validUntil }: Omit<CargoCreateEditDialogProps, "type">) => {

	const [ loading, setLoading ] = useState<boolean>(false);

	const theme = useTheme();

	return (
		<FormDialog
			form="cargo-form"
			maxWidth="xl"
			title={intl.formatMessage({ id: "CARGO" })}
			open={open}
			dialogStyling={{ content: { backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#EEE" } }}
			onClose={() => onClose(false)}
			loading={loading}>
			<CargoCreateEdit
				linkedCharter={charter}
				pageInDialogSettings={{
					entityId: cargoId,
					onLoadingChange: (isLoading) => setLoading(isLoading),
					onSuccess: (response) => {
						onClose(false);
						refetch(response as FormCargo);
					},
				}}
				validFrom={validFrom}
				validUntil={validUntil}
			/>
		</FormDialog>
	);
};

export default CargoCreateEditDialog;
